import React, { useRef } from "react";
import { Box, Typography, Button, Container, Chip } from "@mui/material";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import routes from "../../../Config/routes";
import {
  Flag,
  Collections,
  RateReview,
  SmartToy,
  Group,
  BarChart,
  Contacts,
} from "@mui/icons-material";
import { useTheme } from '@mui/material/styles';
import { BentoGrid, BentoGridItem } from "../../../Components/BentoGrid";

const CreateSection = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const theme = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const creativeModules = [
    {
      title: "Creative Briefs",
      description: "Dynamic brief builder with pre-configured templates for instant campaign launch",
      icon: <Flag sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      colSpan: 1,
    },
    {
      title: "Asset Library",
      description: "Centralized media repo with tags, drafts, comments, and locked archives. Organize all your creative assets in one place.",
      icon: <Collections sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />,
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      colSpan: 2,
    },
    {
      title: "Review Workflows",
      description: "Multi-stage approvals with in-app annotations and stakeholder comments",
      icon: <RateReview sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />,
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      colSpan: 1,
    },
    {
      title: "AI Creation",
      description: "Smart copy generator and one-click reflow for any aspect ratio. Generate content ideas, captions, and creative variations instantly.",
      icon: <SmartToy sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />,
      gradient: "linear-gradient(135deg, #feca57 0%, #ff9ff3 100%)",
      colSpan: 2,
    },
    {
      title: "Team Collab",
      description: "Real-time tasks, threads, and granular role permissions",
      icon: <Group sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />,
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      colSpan: 1,
    },
    {
      title: "Analytics",
      description: "Engagement dashboards and ROI tracking with budget tagging",
      icon: <BarChart sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />,
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      colSpan: 1,
    },
    {
      title: "CRM Tools",
      description: "Creator profiles, deal history, and automated follow-ups",
      icon: <Contacts sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />,
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      colSpan: 1,
    },
  ];
   <BentoGrid>
              {creativeModules.map((module, index) => (
                <BentoGridItem
                  key={module.title}
                  title={module.title}
                  description={module.description}
                  icon={module.icon}
                  gradient={module.gradient}
                  colSpan={module.colSpan}
                  header={
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        background: module.gradient,
                        opacity: 0.08,
                        borderRadius: { xs: "8px", sm: "10px", md: "12px" },
                      }}
                    />
                  }
                />
              ))}
            </BentoGrid>
  // <BentoGrid>
  //   {creativeModules.map((module, index) => (
  //     <BentoGridItem
  //       key={module.title}
  //       title={module.title}
  //       description={module.description}
  //       icon={module.icon}
  //       gradient={module.gradient}
  //       colSpan={module.colSpan}
  //       header={
  //         <Box
  //           sx={{
  //             width: "100%",
  //             height: "100%",
  //             background: module.gradient,
  //             opacity: 0.08,
  //             borderRadius: { xs: "8px", sm: "10px", md: "12px" },
  //           }}
  //         />
  //       }
  //     />
  //   ))}
  // </BentoGrid>


  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        py: { xs: 4, sm: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 0 },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)"
        }
      }}
    >
      {/* Background orbs */}
      <Box sx={{
        position: "absolute",
        top: "10%",
        right: "5%",
        width: { xs: "200px", sm: "350px", md: "600px" },
        height: { xs: "200px", sm: "350px", md: "600px" },
        background: "radial-gradient(circle, rgba(102,126,234,0.12) 0%, transparent 70%)",
        filter: "blur(70px)",
        animation: "float 20s ease-in-out infinite"
      }} />
      <Box sx={{
        position: "absolute",
        bottom: "15%",
        left: "5%",
        width: { xs: "180px", sm: "300px", md: "500px" },
        height: { xs: "180px", sm: "300px", md: "500px" },
        background: "radial-gradient(circle, rgba(240,147,251,0.1) 0%, transparent 70%)",
        filter: "blur(60px)",
        animation: "float 16s ease-in-out infinite reverse"
      }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 5 } }}>
            <motion.div variants={itemVariants}>
              <Chip
                label="CONTENT CREATION"
                sx={{
                  mb: { xs: 2, md: 3 },
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  fontWeight: 700,
                  fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" },
                  px: { xs: 1.5, md: 2 },
                  py: 0.5,
                  height: "auto"
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "Moret Variable",
                  fontWeight: '600',
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem", lg: "62px" },
                  color: "#0f172a",
                  mb: { xs: 2, md: 3 },
                  letterSpacing: "-0.02em"
                }}
              >
                BLITZ CREATE
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography
                variant="h6"
                sx={{
                  color: "#475569",
                  mb: 2,
                  maxWidth: "800px",
                  mx: "auto",
                  lineHeight: 1.7,
                  fontSize: { xs: "0.9375rem", sm: "1.0625rem", md: "1.375rem" },
                  fontWeight: 300,
                  px: { xs: 1, sm: 2, md: 0 }
                }}
              >
                Power your creative workflow from <Box component="span" sx={{ color: "#667eea", fontWeight: 600 }}>briefs to publish</Box>—streamline ideation, review, and launch with <Box component="span" sx={{ color: "#f5576c", fontWeight: 600 }}>AI-powered tools</Box>
              </Typography>
            </motion.div>
          </Box>

          {/* Modules Bento Grid */}
          <Box sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#0f172a",
                  mb: { xs: 3, sm: 4, md: 5 },
                  textAlign: "center",
                  fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" }
                }}
              >
                Complete Toolkit for Modern Creators
              </Typography>
            </motion.div>

            <BentoGrid>
              {creativeModules.map((module, index) => (
                <BentoGridItem
                  key={module.title}
                  title={module.title}
                  description={module.description}
                  icon={module.icon}
                  gradient={module.gradient}
                  colSpan={module.colSpan}
                  header={
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        background: module.gradient,
                        opacity: 0.08,
                        borderRadius: { xs: "8px", sm: "10px", md: "12px" },
                      }}
                    />
                  }
                />
              ))}
            </BentoGrid>
          </Box>

          {/* Bottom Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center", mb: { xs: 3, md: 4 } }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#64748b",
                  mb: { xs: 2, md: 3 },
                  fontWeight: 300,
                  maxWidth: "700px",
                  mx: "auto",
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                  px: { xs: 1, sm: 2, md: 0 }
                }}
              >
                Join thousands of teams elevating their content creation process with seamless workflows and intelligent automation
              </Typography>
            </Box>
          </motion.div>

          {/* CTA */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(routes.onboard)}
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "0.875rem", sm: "1rem", md: "1.125rem" },
                  px: { xs: 3, sm: 5, md: 8 },
                  py: { xs: 1.5, sm: 2, md: 2.5 },
                  borderRadius: "50px",
                  boxShadow: { xs: "0 8px 24px rgba(0,0,0,0.12)", md: "0 12px 40px rgba(0,0,0,0.15)" },
                  position: "relative",
                  overflow: "hidden",
                  textTransform: "none",
                  width: { xs: "100%", sm: "auto" },
                  '&::before': {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    transition: "left 0.5s",
                  },
                  '&:hover': {
                    bgcolor: theme.palette.primary.light,
                    transform: { xs: "none", sm: "translateY(-4px) scale(1.02)" },
                    boxShadow: { xs: "0 8px 24px rgba(0,0,0,0.15)", md: "0 16px 56px rgba(0,0,0,0.2)" },
                    '&::before': {
                      left: '100%'
                    }
                  },
                  transition: "all 0.3s ease"
                }}
              >
                <span style={{ position: "relative", zIndex: 1 }}>
                  Start Creating Today
                </span>
              </Button>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CreateSection;